import { ITableHeader, DotLegalOverflowMenu, DotLegalEmptyState } from "@dotlegal/dotlegal-ui-components";
import { Box, TableCell } from "@mui/material";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import DotLegalLink from "../../../common/components/dotLegalLink/DotLegalLink";
import QuestionnaireEvaluation from "../../../common/components/questionnaireComponents/questionnaireEvaluation/QuestionnaireEvaluation";
import QuestionnaireReportButtons from "../../../common/components/questionnaireComponents/questionnaireReportButtons/QuestionnaireReportButtons";
import QuestionnaireReportOverflowButtons from "../../../common/components/questionnaireComponents/questionnaireReportButtons/QuestionnaireReportOverflowButtons";
import { warning } from "@common/icons.tsx";
import { useTranslation } from "@localization/useTranslation.ts";
import { useRiskIndicator } from "../../../riskAssessment/riskIndicator/RiskIndicator.hooks";
import { useUrlProvider } from "../../../useUrlProvider";
import { toLegalEntityScope } from "../../legalEntities/LegalEntities.types";
import EmptyState from "../../../common/svgs/emptyState.svg?react";
import QuestionnaireTemplateLink from "../../legalEntityAuditTab/QuestionnaireTemplateLink";
import DeleteClassificationDialog from "../deleteClassificationDialog/DeleteClassificationDialog";
import EvaluateClassificationDialog from "../evaluateClassificationDialog/EvaluateClassificationDialog";
import {
    LegalEntityClassificationApiModel,
    LegalEntityClassificationApiRow,
    LegalEntityClassificationViewRow,
    RiskLevel,
} from "../LegalEntityClassificationTab.types";
import ReopenClassificationDialog from "../reopenClassificationDialog/ReopenClassificationDialog";
import useClassificationTableHooks from "./ClassificationTable.hooks";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import { PlanLimit } from "../../../auth/planProvider/PlanProvider.types.ts";
import TrialUsageWarning from "../../../common/components/TrialUsageWarning.tsx";
import TrialExpiredRowWrapper from "../../../common/components/TrialExpiredRowWrapper.tsx";
import { EntityType } from "@common/Common.types.ts";
import { useAddTaskMenuItem } from "@common/components/addTaskMenuItem/AddTaskMenuItemContext.hooks.ts";
import DotLegalNoteDialog from "@common/components/dotlegalNote/dotLegalNoteDialog/DotLegalNoteDialog";
import TruncatedMentionText from "@common/components/mentionInput/TruncatedMentionText";
import { noteExcludedFromSearch } from "@common/tableSearch";
import ProcessingActivityLinks from "../../../processingActivity/processingActivityLinks/ProcessingActivityLinks.tsx";
import SystemLinks from "../../../system/SystemLinks.tsx";

export interface IClassificationTableProps {
    data: LegalEntityClassificationApiModel | undefined;
    isLoading: boolean;
    refetch: () => void;
    pageType: "legalEntity" | "system" | "processingActivity";
    emptyStateText: string;
    planLimit?: PlanLimit;
    sessionStorageKey?: string;
}

function ClassificationTable(props: IClassificationTableProps) {
    const addTaskMenuItem = useAddTaskMenuItem();
    const { translateString } = useTranslation();
    const { getLegalEntityClassificationUrl, getLegalEntityUrl } = useUrlProvider();
    const {
        hasData,
        classifications,
        selectedClassification,
        setSelectedClassification,
        setShowDeleteDialog,
        showDeleteDialog,
        onClassificationDeleted,
        getReportPdfUrl,
        isDownloadingPdf,
        onDownloadReportAsPdf,
        showEvaluateDialog,
        setShowEvaluateDialog,
        onClassificationEvaluated,
        setShowReopenDialog,
        showReopenDialog,
        getFilterOptions,
        onFilterChange,
        hasActiveFilters,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        getNoteMenuItem,
        noteDialogProps,
    } = useClassificationTableHooks(props);

    const { getRiskIcon } = useRiskIndicator();

    function getMenuItems(row: LegalEntityClassificationApiRow) {
        const legalEntityPermissions = row.permissions;

        let menuItems = [];

        if (legalEntityPermissions.delete) {
            menuItems.push({
                menuItem: translateString("deleteClassification"),
                onClick: () => {
                    setSelectedClassification(row);
                    setShowDeleteDialog(true);
                },
            });
        }

        if (row.partOfCurrentPlan) {
            // A classification has no page of its own, so the menu is the only way to its note.
            if (legalEntityPermissions.edit) {
                menuItems.push(getNoteMenuItem(row));
            }

            if (row.submitted && legalEntityPermissions.edit) {
                menuItems.push({
                    menuItem: translateString("evaluateClassificationHeader"),
                    onClick: () => {
                        setSelectedClassification(row);
                        setShowEvaluateDialog(true);
                    },
                });
            }

            if (row.submitted && legalEntityPermissions.edit) {
                menuItems.push({
                    menuItem: translateString("reopenCompletedClassification"),
                    onClick: () => {
                        setSelectedClassification(row);
                        setShowReopenDialog(true);
                    },
                });
            }
        }

        addTaskMenuItem.addCreateTaskMenuItem(menuItems, {
            fromEntityId: row.id,
            fromEntityType: EntityType.LegalEntityClassification,
            toEntityType: EntityType.Task,
        });

        return menuItems;
    }

    function formatUrl(id: string, name: string, url: string) {
        return (
            <Box key={id} component={"span"} sx={{ fontWeight: "bold" }}>
                <DotLegalLink linkColor={"primary"} to={url}>
                    {name}
                </DotLegalLink>
            </Box>
        );
    }

    function getRisk(row: LegalEntityClassificationApiRow) {
        var icon = getIcon(row);
        var name = getRiskName(row);

        if (!row.submitted) {
            return (
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    {icon}
                    <Box sx={{ fontWeight: 700 }}>
                        <DotLegalLink
                            linkColor={"primary"}
                            to={getLegalEntityClassificationUrl(
                                toLegalEntityScope(row.legalEntity.isGroupEntity),
                                row.legalEntity.legalEntityId,
                                row.questionnaireResponseId
                            )}
                        >
                            {name}
                        </DotLegalLink>
                    </Box>
                </Box>
            );
        }
        return (
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                {icon}
                <Box>{name}</Box>
            </Box>
        );
    }

    function getIcon(row: LegalEntityClassificationApiRow) {
        if (row.riskLevel || row.riskLevel === 0) {
            return getRiskIcon(row.riskLevel);
        }

        if (!row.scoreEnabled && row.submitted) return getRiskIcon(RiskLevel.None);

        return warning;
    }

    function getRiskName(row: LegalEntityClassificationApiRow) {
        if (row.riskName) {
            return row.riskName;
        }

        if (!row.scoreEnabled && row.submitted) return translateString("none");

        return translateString("answerClassification");
    }

    function getReportButtons(row: LegalEntityClassificationApiRow) {
        if (!row.partOfCurrentPlan) {
            return undefined;
        }

        const viewModel = {
            id: row.id,
            questionnaireName: row.questionnaireName,
            legalEntityName: row.legalEntity.name,
            questionnaireResponseId: row.questionnaireResponseId,
        };

        if (row.hasRecommendations) {
            return (
                <QuestionnaireReportOverflowButtons
                    reportPdfUrl={getReportPdfUrl(viewModel, false, true)}
                    reportPdfMenuItems={[
                        { menuItem: translateString("downloadWithRecommendations"), onClick: () => onDownloadReportAsPdf(viewModel, true) },
                        {
                            menuItem: translateString("downloadWithoutRecommendations"),
                            onClick: () => onDownloadReportAsPdf(viewModel, false),
                        },
                    ]}
                    showButtons
                    isDownloadingPdf={isDownloadingPdf(row.id)}
                />
            );
        }

        return (
            <QuestionnaireReportButtons
                showButtons
                reportPdfUrl={getReportPdfUrl(viewModel, false, false)}
                onDownloadReportPdf={() => onDownloadReportAsPdf(viewModel, false)}
                isDownloadingPdf={isDownloadingPdf(row.id)}
            />
        );
    }

    function getHeaders() {
        const headers = Array<ITableHeader<LegalEntityClassificationViewRow>>();
        headers.push({ property: "questionnaireName", text: translateString("template"), align: "left", showOnMobile: true });
        headers.push({ property: "created", text: translateString("created"), align: "left", showOnMobile: false });

        if (props.pageType === "legalEntity") {
            headers.push({ property: "relatedEntityName", text: translateString("assetOrProcessingActivity"), align: "left", showOnMobile: true });
        } else {
            headers.push({ property: "legalEntityName", text: translateString("legalEntity"), align: "left", showOnMobile: true });
        }

        headers.push({ property: "riskLevel", text: translateString("riskLevel"), align: "left", showOnMobile: true });
        headers.push({ property: "score", text: translateString("score"), align: "left", showOnMobile: true });
        headers.push({ property: "submitted", text: translateString("submitted"), align: "left", showOnMobile: true });
        headers.push({ property: "submittedBy", text: translateString("submittedBy"), align: "left", showOnMobile: true });
        headers.push({ property: "evaluation", text: translateString("evaluation"), align: "left", showOnMobile: true });

        headers.push({ property: "evaluationName", text: "", align: "left", showOnMobile: true, hideHeader: true });

        headers.push({ property: "report", text: translateString("classificationReport"), align: "left", showOnMobile: true });
        headers.push({ property: "notePlainText", text: translateString("note"), align: "left", showOnMobile: true });
        return headers;
    }

    return (
        <>
            {hasData ? (
                <>
                    {props.planLimit?.limitExceeded && <TrialUsageWarning textKey={"classificationLimitExceededWarning"} marginBottom={2} />}
                    <DotLegalTableWithFilterBar
                        filterBar={{
                            isLoading: props.isLoading,
                            filterData: getFilterOptions(),
                            fetchFilterData: true,
                            onMenuClose: onFilterChange,
                            onRemove: onFilterChange,
                            pinnedFilterIds: pinnedFilterIds,
                            onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                            onClearFilter: hasActiveFilters ? () => onFilterChange(getFilterOptions().map((o) => ({ ...o, value: null }))) : undefined,
                            clearFilterText: hasActiveFilters ? "defaultView" : undefined,
                        }}
                        headers={getHeaders()}
                        columnsExcludedFromFilter={noteExcludedFromSearch}
                        defaultOrderBy={"created"}
                        data={classifications}
                        isLoading={props.isLoading}
                        defaultOrder={"desc"}
                        rowsPerPage={30}
                        clickableRows={false}
                        hasOverflowMenu
                        hidePagination
                        emptyText={translateString(hasActiveFilters ? "noDataMatchingCriteria" : "noData")}
                        renderRow={(row, i) => (
                            <TrialExpiredRowWrapper
                                key={row.id}
                                tooltip={translateString("classificationLocked")}
                                onNonExpiredClick={() => {}}
                                expired={!row.partOfCurrentPlan}
                                planUpgradeText={translateString("classificationLimitReached")}
                            >
                                <TableCell>
                                    <QuestionnaireTemplateLink type={"classification"} id={row.questionnaireId} name={row.questionnaireName} />
                                </TableCell>
                                <TableCell>{row.createdString}</TableCell>
                                {props.pageType === "legalEntity" && (
                                    <TableCell>
                                        {row.system && <SystemLinks tab={"3"} systems={[row.system]} />}
                                        {row.processingActivity && <ProcessingActivityLinks tab={"3"} processingActivityLinks={[row.processingActivity]} />}
                                    </TableCell>
                                )}

                                {(props.pageType === "system" || props.pageType === "processingActivity") && (
                                    <TableCell>
                                        {row.legalEntity && (
                                            <DotLegalLink bold linkColor={"primary"} to={getLegalEntityUrl(row.legalEntity.legalEntityId)}>
                                                {row.legalEntity.name}
                                            </DotLegalLink>
                                        )}
                                    </TableCell>
                                )}

                                <TableCell>{row.partOfCurrentPlan && getRisk(row)}</TableCell>
                                <TableCell align="right">{row.scoreName}</TableCell>
                                <TableCell>{row.submittedString}</TableCell>
                                <TableCell>{row.submittedBy}</TableCell>
                                <TableCell>
                                    <QuestionnaireEvaluation
                                        evaluation={row.partOfCurrentPlan ? row.evaluation : undefined}
                                        evaluationStatus={row.evaluationStatus}
                                    />
                                </TableCell>
                                <TableCell>{getReportButtons(row)}</TableCell>
                                <TableCell>{row.partOfCurrentPlan && <TruncatedMentionText value={row.note} />}</TableCell>
                                <TableCell align={"right"}>{row.permissions.edit && <DotLegalOverflowMenu items={getMenuItems(row)} />}</TableCell>
                            </TrialExpiredRowWrapper>
                        )}
                        paginationLabelOf={""}
                    ></DotLegalTableWithFilterBar>
                </>
            ) : (
                <Box
                    sx={{
                        img: {
                            width: "15%",
                        },
                    }}
                >
                    <DotLegalEmptyState icon={<EmptyState />} text={props.emptyStateText ?? translateString("legalEntityClassificationEmptyState")} />
                </Box>
            )}
            {showDeleteDialog && (
                <DeleteClassificationDialog
                    onDialogClose={() => setShowDeleteDialog(false)}
                    selectedClassification={selectedClassification!}
                    onSuccessDelete={onClassificationDeleted}
                />
            )}

            {showEvaluateDialog && (
                <EvaluateClassificationDialog
                    onEvaluated={onClassificationEvaluated}
                    onDialogClose={() => {
                        setShowEvaluateDialog(false);
                        setSelectedClassification(undefined);
                    }}
                    selectedClassification={selectedClassification!}
                />
            )}
            {showReopenDialog && (
                <ReopenClassificationDialog
                    classificationId={selectedClassification!.id}
                    name={selectedClassification!.questionnaireName}
                    onDialogClose={() => setShowReopenDialog(false)}
                    onInternalAssessmentReopened={() => setShowReopenDialog(false)}
                />
            )}

            {noteDialogProps && <DotLegalNoteDialog {...noteDialogProps} />}
        </>
    );
}

export default ClassificationTable;
